/**
 * IndexedDB Quota Monitor
 *
 * Monitors IndexedDB storage quota usage and warns before limits are reached.
 * Critical for preventing data loss during migration and normal operation.
 *
 * Features:
 * - Real-time quota checking
 * - Warning thresholds (80%, 95%)
 * - Persistent quota history
 * - Usage estimates per module
 *
 * Part of: IndexedDB Phase 3.6 Migration - Week 0 Safety Infrastructure
 * Created: 2026-01-30
 */

// Warning thresholds
const WARN_THRESHOLD = 0.8; // 80% usage
const CRITICAL_THRESHOLD = 0.95; // 95% usage

// Cache quota info to avoid repeated checks
let cachedQuotaInfo = null;
let lastCheckTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

/**
 * Get current IndexedDB quota information
 *
 * @returns {Promise<{quota: number, usage: number, available: number, percentUsed: number, status: string}>}
 */
export async function getQuotaInfo() {
  // Return cached result if recent
  const now = Date.now();
  if (cachedQuotaInfo && (now - lastCheckTime) < CACHE_DURATION) {
    return cachedQuotaInfo;
  }

  try {
    // Check if Storage API is available
    if (!navigator.storage || !navigator.storage.estimate) {
      return {
        quota: 0,
        usage: 0,
        available: 0,
        percentUsed: 0,
        status: 'unavailable',
        error: 'StorageManager.estimate() not supported in this browser'
      };
    }

    // Get quota estimate
    const estimate = await navigator.storage.estimate();

    const quota = estimate.quota || 0;
    const usage = estimate.usage || 0;
    const available = quota - usage;
    const percentUsed = quota > 0 ? usage / quota : 0;

    // Determine status based on thresholds
    let status = 'ok';
    if (percentUsed >= CRITICAL_THRESHOLD) {
      status = 'critical';
    } else if (percentUsed >= WARN_THRESHOLD) {
      status = 'warning';
    }

    const quotaInfo = {
      quota,
      usage,
      available,
      percentUsed,
      status,
      timestamp: now,
      // Human-readable sizes
      quotaMB: Math.round(quota / 1024 / 1024),
      usageMB: Math.round(usage / 1024 / 1024),
      availableMB: Math.round(available / 1024 / 1024)
    };

    // Cache the result
    cachedQuotaInfo = quotaInfo;
    lastCheckTime = now;

    return quotaInfo;
  } catch (error) {
    return {
      quota: 0,
      usage: 0,
      available: 0,
      percentUsed: 0,
      status: 'error',
      error: error.message
    };
  }
}

/**
 * Check if there's enough quota for a write operation
 *
 * @param {number} estimatedSizeBytes - Estimated size of data to write
 * @returns {Promise<{allowed: boolean, reason?: string, quotaInfo: Object}>}
 */
export async function checkQuotaBeforeWrite(estimatedSizeBytes = 0) {
  const quotaInfo = await getQuotaInfo();

  // If quota API unavailable, allow write (fail-open for compatibility)
  if (quotaInfo.status === 'unavailable' || quotaInfo.status === 'error') {
    return {
      allowed: true,
      reason: 'Quota monitoring unavailable, allowing write',
      quotaInfo
    };
  }

  // Check if write would exceed quota
  const projectedUsage = quotaInfo.usage + estimatedSizeBytes;
  const projectedPercent = projectedUsage / quotaInfo.quota;

  if (projectedPercent >= 1.0) {
    return {
      allowed: false,
      reason: `Write would exceed quota (${Math.round(projectedPercent * 100)}%)`,
      quotaInfo
    };
  }

  // Check if write would push into critical zone
  if (projectedPercent >= CRITICAL_THRESHOLD) {
    return {
      allowed: false,
      reason: `Write would exceed critical threshold (${Math.round(projectedPercent * 100)}% of quota)`,
      quotaInfo
    };
  }

  // Warn if write would push into warning zone
  if (projectedPercent >= WARN_THRESHOLD && quotaInfo.percentUsed < WARN_THRESHOLD) {
    console.warn(`[QuotaMonitor] Write will push quota into warning zone (${Math.round(projectedPercent * 100)}%)`);
  }

  return {
    allowed: true,
    quotaInfo
  };
}

/**
 * Get user-friendly quota status message
 *
 * @param {Object} quotaInfo - Result from getQuotaInfo()
 * @returns {string} - User-friendly message
 */
export function getQuotaMessage(quotaInfo) {
  if (!quotaInfo) {
    return 'Quota information unavailable';
  }

  if (quotaInfo.status === 'unavailable') {
    return 'Storage quota monitoring not supported in this browser';
  }

  if (quotaInfo.status === 'error') {
    return `Quota check failed: ${quotaInfo.error}`;
  }

  const percent = Math.round(quotaInfo.percentUsed * 100);
  const usedMB = quotaInfo.usageMB;
  const totalMB = quotaInfo.quotaMB;

  switch (quotaInfo.status) {
    case 'critical':
      return `⚠️ Storage critically low: ${usedMB}MB / ${totalMB}MB (${percent}% used). Please free up space.`;

    case 'warning':
      return `⚠️ Storage high: ${usedMB}MB / ${totalMB}MB (${percent}% used). Consider clearing old data.`;

    case 'ok':
    default:
      return `Storage: ${usedMB}MB / ${totalMB}MB (${percent}% used)`;
  }
}

/**
 * Estimate storage size of an object
 * Rough heuristic - actual IndexedDB overhead may vary
 *
 * @param {*} obj - Object to estimate size of
 * @returns {number} - Estimated size in bytes
 */
export function estimateObjectSize(obj) {
  if (!obj) return 0;

  try {
    // Serialize to JSON and measure length
    const jsonString = JSON.stringify(obj);

    // Rough heuristic: 2 bytes per character (UTF-16) + 20% IndexedDB overhead
    const sizeBytes = jsonString.length * 2 * 1.2;

    return Math.ceil(sizeBytes);
  } catch (error) {
    console.warn('[QuotaMonitor] Failed to estimate object size:', error);
    return 0; // Fail-open
  }
}

/**
 * Clear cached quota info (force fresh check on next call)
 */
export function clearQuotaCache() {
  cachedQuotaInfo = null;
  lastCheckTime = 0;
}

/**
 * Get detailed breakdown by IndexedDB database
 * Note: This requires iterating all databases and measuring their size
 *
 * @returns {Promise<Array<{name: string, size: number}>>}
 */
export async function getUsageByDatabase() {
  try {
    // Get list of all IndexedDB databases
    if (!indexedDB.databases) {
      return [{
        name: 'unknown',
        size: 0,
        note: 'Database enumeration not supported in this browser'
      }];
    }

    const databases = await indexedDB.databases();
    const usageDetails = [];

    for (const dbInfo of databases) {
      try {
        const db = await new Promise((resolve, reject) => {
          const request = indexedDB.open(dbInfo.name);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        // Rough estimate: count records across all object stores
        let recordCount = 0;
        for (const storeName of db.objectStoreNames) {
          const tx = db.transaction(storeName, 'readonly');
          const store = tx.objectStore(storeName);
          const count = await new Promise((resolve) => {
            const countReq = store.count();
            countReq.onsuccess = () => resolve(countReq.result);
            countReq.onerror = () => resolve(0);
          });
          recordCount += count;
        }

        usageDetails.push({
          name: dbInfo.name,
          version: dbInfo.version,
          recordCount,
          estimatedSize: recordCount * 1024 // Very rough estimate: 1KB per record
        });

        db.close();
      } catch (error) {
        usageDetails.push({
          name: dbInfo.name,
          error: error.message
        });
      }
    }

    return usageDetails;
  } catch (error) {
    console.warn('[QuotaMonitor] Failed to get usage by database:', error);
    return [{
      error: error.message
    }];
  }
}

// Expose to window for debugging in development
if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
  window.quotaMonitor = {
    getQuotaInfo,
    checkQuotaBeforeWrite,
    getQuotaMessage,
    estimateObjectSize,
    clearQuotaCache,
    getUsageByDatabase
  };
}
